import { makeAutoObservable, runInAction } from "mobx";
import { changeDpiBlob } from "changedpi";
import {
  base64ToBlob,
  CommandAction,
  hexToRgba,
  loadImage,
  loadImageUrl,
  resizeImage,
  Vec2,
  Webgl2New,
  CanvasDrawImageSource,
  Nullable,
} from "src/image-editor-core";

import { getMedia, graphqlClient, postRetouchAISingle, postSingleRemoveDust } from "src/api";
import { DeviceMetaDataModel, MediaShortModel } from "src/models";
import { pushDataLayerEvent, removeImageBackground } from "src/utils";
import { useStores } from "src/hooks";
import { ErrorCodes } from "src/constants";
import {
  openDustRemovalErrorNotification,
  openRetouchErrorNotification,
} from "src/containers/media/media.utils";
import {
  EditMediaPageHistoryState,
  EditMediaTabBlockCallback,
  EditMediaTabType,
} from "./edit-media.types";
import { transparentBackground } from "../create/remove-background/remove-background.constants";
import { createRequest } from "urql";
import {
  AI_CHANGE_METAL_MATERIAL,
  AI_LIFESTYLE_IMAGE,
  AI_MODEL_IMAGE,
  AI_TASK_STATE,
} from "src/api/graphql-api/user/user.gql";
import { MEDIA_SET_SKU, PRODUCT_GEN_SKU } from "src/api/graphql-api/product/product.gql";

const BG_CUSTOM_COLORS_STORAGE_KEY = "edit-media.background.custom-colors";

/** `changeDpiBlob` uses FileReader without `onerror`; race a timeout so awaiters never hang. */
const CHANGEDPI_BLOB_TIMEOUT_MS = 15000;

const DPI_300_TOLERANCE_MIN = 295;
const DPI_300_TOLERANCE_MAX = 305;

function promiseWithTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise
      .then((v) => {
        window.clearTimeout(t);
        resolve(v);
      })
      .catch((e) => {
        window.clearTimeout(t);
        reject(e);
      });
  });
}

function readU32BE(u8: Uint8Array, offset: number): number {
  return (
    ((u8[offset] << 24) | (u8[offset + 1] << 16) | (u8[offset + 2] << 8) | u8[offset + 3]) >>> 0
  );
}

function isApprox300Dpi(xDpi: number, yDpi: number): boolean {
  return (
    xDpi >= DPI_300_TOLERANCE_MIN &&
    xDpi <= DPI_300_TOLERANCE_MAX &&
    yDpi >= DPI_300_TOLERANCE_MIN &&
    yDpi <= DPI_300_TOLERANCE_MAX
  );
}

/**
 * API may send non-boolean (e.g. "false", "0"); `Boolean("false")` is true — parse explicitly.
 * Unknown shapes return null so we fall back to embedded file DPI.
 */
function parseWorkflowDpi300(raw: unknown): boolean | null {
  if (raw === true || raw === 1) return true;
  if (raw === false || raw === 0) return false;
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (s === "true" || s === "1") return true;
    if (s === "false" || s === "0" || s === "") return false;
    return null;
  }
  if (raw === null || raw === undefined) return null;
  return null;
}

/** `shootingConfig.workflow.dpi300`; null if absent or unparsable (fall back to embedded DPI). */
function readDpi300PreferenceFromMeta(meta: DeviceMetaDataModel): boolean | null {
  const sc = meta.shootingConfig;
  if (sc && typeof sc === "object" && !Array.isArray(sc)) {
    const workflow = (sc as Record<string, unknown>).workflow;
    if (workflow && typeof workflow === "object" && workflow !== null && "dpi300" in workflow) {
      return parseWorkflowDpi300((workflow as Record<string, unknown>).dpi300);
    }
  }
  return null;
}

function pngEmbeddedDpiIsApprox300(u8: Uint8Array): boolean {
  if (u8.length < 33 || u8[0] !== 0x89 || u8[1] !== 0x50 || u8[2] !== 0x4e || u8[3] !== 0x47) {
    return false;
  }
  let offset = 8;
  while (offset + 12 <= u8.length) {
    const len = readU32BE(u8, offset);
    const type = String.fromCharCode(
      u8[offset + 4],
      u8[offset + 5],
      u8[offset + 6],
      u8[offset + 7],
    );
    if (type === "IEND") break;
    if (type === "pHYs" && len === 9 && offset + 8 + 9 + 4 <= u8.length) {
      const xppm = readU32BE(u8, offset + 8);
      const yppm = readU32BE(u8, offset + 12);
      const unit = u8[offset + 16];
      if (unit !== 1) return false;
      const xd = Math.round(xppm * 0.0254);
      const yd = Math.round(yppm * 0.0254);
      return isApprox300Dpi(xd, yd);
    }
    offset += 12 + len;
  }
  return false;
}

function readU16Tiff(u8: Uint8Array, o: number, le: boolean): number {
  return le ? u8[o] | (u8[o + 1] << 8) : (u8[o] << 8) | u8[o + 1];
}

function readU32Tiff(u8: Uint8Array, o: number, le: boolean): number {
  return le
    ? (u8[o] | (u8[o + 1] << 8) | (u8[o + 2] << 16) | (u8[o + 3] << 24)) >>> 0
    : readU32BE(u8, o);
}

/** IFD0 XResolution / YResolution / ResolutionUnit (EXIF), segment-bounded. */
function exifTiffDpiIsApprox300(u8: Uint8Array, tiffStart: number, bufferEnd: number): boolean {
  if (tiffStart + 8 > bufferEnd) return false;
  const le = u8[tiffStart] === 0x49 && u8[tiffStart + 1] === 0x49;
  const be = u8[tiffStart] === 0x4d && u8[tiffStart + 1] === 0x4d;
  if (!le && !be) return false;
  if (readU16Tiff(u8, tiffStart + 2, le) !== 42) return false;
  const ifd0Off = readU32Tiff(u8, tiffStart + 4, le);
  const ifd0 = tiffStart + ifd0Off;
  if (ifd0 < tiffStart || ifd0 + 2 > bufferEnd) return false;
  const n = readU16Tiff(u8, ifd0, le);
  if (ifd0 + 2 + n * 12 > bufferEnd) return false;

  let xNum = 0;
  let xDen = 1;
  let yNum = 0;
  let yDen = 1;
  let haveX = false;
  let haveY = false;
  let unit: number | undefined;

  for (let e = 0; e < n; e++) {
    const ent = ifd0 + 2 + e * 12;
    const tag = readU16Tiff(u8, ent, le);
    const type = readU16Tiff(u8, ent + 2, le);
    const count = readU32Tiff(u8, ent + 4, le);
    const valOrOff = readU32Tiff(u8, ent + 8, le);

    if (tag === 296 && type === 3 && count === 1) {
      unit = readU16Tiff(u8, ent + 8, le);
    } else if ((tag === 282 || tag === 283) && type === 5 && count === 1) {
      const roff = tiffStart + valOrOff;
      if (roff + 8 > bufferEnd) continue;
      const num = readU32Tiff(u8, roff, le);
      const den = readU32Tiff(u8, roff + 4, le);
      if (den === 0) continue;
      if (tag === 282) {
        xNum = num;
        xDen = den;
        haveX = true;
      } else {
        yNum = num;
        yDen = den;
        haveY = true;
      }
    }
  }

  if (!haveX || !haveY || unit === undefined) return false;
  const x = xNum / xDen;
  const y = yNum / yDen;
  if (unit === 2) {
    return isApprox300Dpi(Math.round(x), Math.round(y));
  }
  if (unit === 3) {
    return isApprox300Dpi(Math.round(x * 2.54), Math.round(y * 2.54));
  }
  return false;
}

function exifApp1SegmentDpiIsApprox300(u8: Uint8Array, dataStart: number, segEnd: number): boolean {
  if (dataStart + 6 > segEnd) return false;
  if (
    u8[dataStart] !== 0x45 ||
    u8[dataStart + 1] !== 0x78 ||
    u8[dataStart + 2] !== 0x69 ||
    u8[dataStart + 3] !== 0x66 ||
    u8[dataStart + 4] !== 0 ||
    u8[dataStart + 5] !== 0
  ) {
    return false;
  }
  return exifTiffDpiIsApprox300(u8, dataStart + 6, segEnd);
}

function jpegEmbeddedDpiIsApprox300(u8: Uint8Array): boolean {
  if (u8.length < 4 || u8[0] !== 0xff || u8[1] !== 0xd8) return false;
  let i = 2;
  while (i < u8.length - 1) {
    if (u8[i] !== 0xff) {
      i++;
      continue;
    }
    const marker = u8[i + 1];
    if (marker === 0xff) {
      i++;
      continue;
    }
    if (marker === 0xd9 || marker === 0xda) break;

    if (i + 3 >= u8.length) break;
    const segLen = (u8[i + 2] << 8) | u8[i + 3];
    if (segLen < 2 || i + 2 + segLen > u8.length) break;

    const dataStart = i + 4;
    const segEnd = i + 2 + segLen;

    if (marker === 0xe0) {
      const j = dataStart;
      // JFIF density uses j..j+11 (12 bytes); use <= so the last byte at j+11 is allowed when flush to segment end.
      if (
        j + 12 <= segEnd &&
        j + 12 <= u8.length &&
        u8[j] === 0x4a &&
        u8[j + 1] === 0x46 &&
        u8[j + 2] === 0x49 &&
        u8[j + 3] === 0x46 &&
        u8[j + 4] === 0
      ) {
        const units = u8[j + 7];
        const xd = (u8[j + 8] << 8) | u8[j + 9];
        const yd = (u8[j + 10] << 8) | u8[j + 11];
        if (units === 1 && isApprox300Dpi(xd, yd)) return true;
        if (units === 2) {
          const xDpi = Math.round(xd * 2.54);
          const yDpi = Math.round(yd * 2.54);
          if (isApprox300Dpi(xDpi, yDpi)) return true;
        }
      }
    } else if (marker === 0xe1) {
      if (exifApp1SegmentDpiIsApprox300(u8, dataStart, segEnd)) return true;
    }
    i += 2 + segLen;
  }
  return false;
}

function sniffImageMime(u8: Uint8Array): string {
  if (u8.length >= 4 && u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4e && u8[3] === 0x47) {
    return "image/png";
  }
  if (u8.length >= 2 && u8[0] === 0xff && u8[1] === 0xd8) {
    return "image/jpeg";
  }
  if (
    u8.length >= 12 &&
    u8[0] === 0x52 &&
    u8[1] === 0x49 &&
    u8[2] === 0x46 &&
    u8[3] === 0x46 &&
    u8[8] === 0x57 &&
    u8[9] === 0x45 &&
    u8[10] === 0x42 &&
    u8[11] === 0x50
  ) {
    return "image/webp";
  }
  if (u8.length >= 6 && u8[0] === 0x47 && u8[1] === 0x49 && u8[2] === 0x46) {
    const ver = String.fromCharCode(u8[3], u8[4], u8[5]);
    if (ver === "87a" || ver === "89a") return "image/gif";
  }
  return "application/octet-stream";
}

/** Single fetch for bytes shared with display (avoids Image load + separate fetch). */
async function fetchImageBytesIfPossible(url: string): Promise<ArrayBuffer | null> {
  if (!url) return null;
  try {
    // Matches loadImage(..., true) (crossOrigin anonymous). credentials:include is invalid with ACAO:* per CORS.
    const res = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

/**
 * Parses ~300 DPI from fetched file bytes; decoded HTMLImageElement has no pHYs/JFIF density.
 * On success decodes via blob: URL + loadImage using the same ArrayBuffer.
 */
async function loadOriginalImageAndDetect300Dpi(imageSrc: string): Promise<{
  image: HTMLImageElement;
  embeddedDpiApprox300: boolean;
}> {
  const bytes = await fetchImageBytesIfPossible(imageSrc);
  if (bytes) {
    const u8 = new Uint8Array(bytes);
    const embeddedDpiApprox300 = pngEmbeddedDpiIsApprox300(u8) || jpegEmbeddedDpiIsApprox300(u8);
    const blob = new Blob([bytes], { type: sniffImageMime(u8) });
    const objectUrl = URL.createObjectURL(blob);
    try {
      const image = await loadImage(objectUrl, false);
      return { image, embeddedDpiApprox300 };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
  const image = await loadImage(imageSrc, true);
  return { image, embeddedDpiApprox300: false };
}

class EditMediaStore {
  private _loading: boolean;
  public get loading() {
    return this._loading;
  }

  private _currentTab: EditMediaTabType = "adjustments";
  public get currentTab() {
    return this._currentTab;
  }

  private _tabBlockCallback: Nullable<EditMediaTabBlockCallback> = null;
  private _tabProceedCallback: Nullable<VoidFunction> = null;

  private _metaData: DeviceMetaDataModel;
  public get metaData() {
    this._metaData &&
      (this._metaData.isTransparentBackground = this.currentBGColor === transparentBackground);
    return this._metaData;
  }

  private _isBGRemoved: boolean;
  public get isBGRemoved() {
    return this._isBGRemoved;
  }

  private _inBGAtFirst: boolean;
  public get inBGAtFirst() {
    return this._inBGAtFirst;
  }

  private _isInCrop: boolean;
  public get isInCrop() {
    return this._isInCrop;
  }

  public get isInMeasurement() {
    return this._currentTab === "measurements";
  }

  public get isInResize() {
    return this._currentTab === "resize";
  }

  public get isInBackground() {
    return this._currentTab === "background";
  }

  private _isGenerativeAI = false;
  public get isGenerativeAI() {
    return this._isGenerativeAI;
  }

  private _orignalUrl = "";
  public get orignalUrl() {
    return this._orignalUrl;
  }

  /** Set on load from meta / embedded file DPI; when true, export PNG with 300 DPI metadata. */
  private _wants300DpiExport = false;

  public toggleGenerativeAI = (value: boolean) => {
    runInAction(() => {
      this._isGenerativeAI = value;
    });
  };

  private _aiRecognitionLoading = false;
  public get aiRecognitionLoading() {
    return this._aiRecognitionLoading;
  }

  public toggleAiRecognitionLoading = (value: boolean) => {
    runInAction(() => {
      this._aiRecognitionLoading = value;
    });
  };

  private _gemStudioMediaId?: string | number | null;
  public get gemStudioMediaId() {
    return this._gemStudioMediaId;
  }

  public set gemStudioMediaId(id) {
    runInAction(() => {
      this._gemStudioMediaId = id;
    });
  }

  public mediaShortModel: MediaShortModel;
  public renderer: Webgl2New.Webgl2dRenderer;

  private _defaultColors = [transparentBackground, "#0d0d0d", "#ffffff"];
  private _addedColors: string[] = [];

  public get bgColors() {
    return this._defaultColors.concat(this._addedColors);
  }

  public currentBGColor = this._defaultColors[2];
  public tempBGColor = this.currentBGColor;

  public uploadedBGs: string[] = []; // objectUrls
  public currentUploadedBG = ""; // objectUrl
  private _isProcessingImage = false;
  public get isProcessingImage() {
    return this._isProcessingImage;
  }

  public measurementColors: string[] = [];
  public measurementsArr: Webgl2New.MeasurementComponent[] = [];
  public fantomMeasurement: Webgl2New.MeasurementComponent;
  private _currentMeasurement: Webgl2New.MeasurementComponent;
  public get currentMeasurement() {
    return this._currentMeasurement;
  }

  public set currentMeasurement(measurementComponent) {
    this._currentMeasurement = measurementComponent;
    this.renderer.selectComponent(measurementComponent);
  }

  private _measurementsStraightenMode = true;
  public get measurementsStraightenMode() {
    return this._measurementsStraightenMode;
  }

  private _rulersEnabled = false;
  public get rulersEnabled() {
    return this._rulersEnabled;
  }

  public backgroundComponent: Webgl2New.BackgroundComponent;
  public backgroundImageComponent: Webgl2New.ImageAdjustRectComponent;
  public mainImageComponent: Webgl2New.ImageAdjustRectComponent;
  public measurementsComponent: Webgl2New.MeasurementsComponent;

  constructor() {
    this._initStore();
    makeAutoObservable(this);
  }

  /* Requests ↓ */
  public async loadMedia(mediaId: string, locationState?: EditMediaPageHistoryState) {
    runInAction(() => {
      this._loading = true;
    });

    const { success } = await getMedia.getRequest({ params: { mediaId } }).fetch();
    this._orignalUrl = success?.file?.original ?? "";
    const imageSrc = locationState?.image ?? success?.file?.original ?? "";
    const meta = success?.metaData || {};
    const fromMeta = success ? readDpi300PreferenceFromMeta(meta) : null;

    let originalImage: HTMLImageElement;
    let wants300Dpi = false;
    if (success && fromMeta !== null) {
      wants300Dpi = fromMeta;
      originalImage = await loadImage(imageSrc, true);
    } else {
      const { image, embeddedDpiApprox300 } = await loadOriginalImageAndDetect300Dpi(imageSrc);
      originalImage = image;
      if (success) {
        wants300Dpi = embeddedDpiApprox300;
      }
    }

    runInAction(() => {
      if (success) {
        this.mediaShortModel = success;
        this._metaData = meta;
        this._wants300DpiExport = wants300Dpi;
        const image = this.constrainSizeImage(originalImage);
        const size = new Vec2(image.width, image.height);

        this.measurementsComponent.setMeasurementHeightAndFontSize(size.min());

        this.mainImageComponent.transform.setSize(size);
        this.mainImageComponent.state.texture.loadSource(image);

        this.renderer.setCanvasSize(size, true);
        this.renderer.selectComponent(this.mainImageComponent);
        this._isBGRemoved = this._metaData.isTransparentBackground || false;
        this._isBGRemoved && this.changeBGColor(transparentBackground);
      } else {
        this._wants300DpiExport = false;
      }

      this._loading = false;
    });
  }

  /**
   * Exports canvas as PNG. When `_wants300DpiExport`, embeds 300 DPI via `changeDpiBlob` (with timeout — upstream has no FileReader.onerror).
   * On timeout or any `changeDpiBlob` failure, silently returns the same PNG without 300 DPI metadata. Returns null only if `toBlob` fails.
   */
  public async getEditedImagePngBlob(): Promise<Blob | null> {
    const canvas = this.renderer.export.getCanvasCast();
    let blob: Blob;
    try {
      blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b != null ? resolve(b) : reject(new Error("Canvas toBlob failed"))),
          "image/png",
        );
      });
    } catch {
      return null;
    }

    if (!this._wants300DpiExport) {
      return blob;
    }

    try {
      return await promiseWithTimeout(
        changeDpiBlob(blob, 300),
        CHANGEDPI_BLOB_TIMEOUT_MS,
        "changeDpiBlob",
      );
    } catch {
      return blob;
    }
  }

  public constrainSizeImage(image: CanvasDrawImageSource) {
    const w = Webgl2New.maxCanvasSize;
    const h = Webgl2New.maxCanvasSize;

    if (image.width > w || image.height > h) {
      if (image.width > image.height) {
        const ratio = w / image.width;
        return resizeImage(image, w, Math.round(image.height * ratio));
      } else {
        const ratio = h / image.height;
        return resizeImage(image, Math.round(image.height * ratio), h);
      }
    }

    return image;
  }

  public removeBG(_params: { mediaId: number }) {
    if (this._isBGRemoved) return;

    const { notificationStore, localeStore, userStore } = useStores();

    notificationStore.openLoader({
      loaderType: "diamond-loader",
      appearance: "secondary",
      title: localeStore.t('["edit-media"].loader.title'),
      subtitle: localeStore.t('["edit-media"].loader.subtitle'),
    });

    const oldImage = this.mainImageComponent.state.texture.textureToCanvas();
    const prevWidth = oldImage.width;
    const prevHeight = oldImage.height;

    // NOTE: We must run AI on the current editor image (incl. crop),
    // so don't use media_id-only mode which would ignore local edits.
    removeImageBackground(oldImage)
      .then(({ image }) => {
        // Note: silently updating userMe due to usage limits
        userStore.loadUserMeSilently();

        let nextImage = image;
        if (nextImage.width !== prevWidth || nextImage.height !== prevHeight) {
          nextImage = resizeImage(nextImage, prevWidth, prevHeight);
        }

        const actionTexture = new Webgl2New.Texture();
        actionTexture.loadSyncSource(nextImage);

        const undoTexture = this.mainImageComponent.state.texture.clone();

        this.renderer.commands.execute(
          new CommandAction(
            "background-removal",
            () => {
              runInAction(() => {
                this.mainImageComponent.state.texture = actionTexture.clone();
                this._isBGRemoved = true;
                this.openBackgroundTab();
                this._metaData = { ...this._metaData, isAiBackgroundRemoval: true };
              });
            },
            () => {
              runInAction(() => {
                this.mainImageComponent.state.texture = undoTexture.clone();
                this._isBGRemoved = false;
                this._metaData = { ...this._metaData, isAiBackgroundRemoval: false };
              });
            },
          ),
        );

        this._inBGAtFirst = true;
      })
      .finally(() => notificationStore.closeLoader());
  }

  public retouchAI(params: { mediaId?: string }) {
    const { mediaId } = params;
    const { notificationStore, localeStore, userStore } = useStores();

    notificationStore.openLoader({
      title: localeStore.t('["edit-media"].loader.title'),
      subtitle: localeStore.t('["edit-media"].loader.subtitle'),
      loaderType: "diamond-loader",
      appearance: "secondary",
    });

    const oldImage = this.mainImageComponent.state.texture.textureToCanvas();
    const prevWidth = oldImage.width;
    const prevHeight = oldImage.height;
    const base64 = oldImage.toDataURL();
    const blob = base64ToBlob(base64.split(",")[1], "image/png");
    const formData = new FormData();
    formData.append("files", blob);

    if (mediaId) {
      formData.append("media_ids", mediaId.toString());
    }

    const request = postRetouchAISingle.getRequest({ data: formData });

    request.events.on("success", async ({ success }) => {
      // Note: silently updating userMe due to usage limits
      userStore.loadUserMeSilently();

      let retouchedImg: CanvasDrawImageSource = await loadImage(
        `data:image/png;base64,${success}`,
        true,
      );

      // NOTE: retouch AI is limited to 1536x1536 maximum resolution,
      // so resize image to source size before actual retouching
      if (retouchedImg.width !== prevWidth || retouchedImg.height !== prevHeight) {
        retouchedImg = resizeImage(retouchedImg, prevWidth, prevHeight);
      }

      pushDataLayerEvent({ event: "retouch_ai" });

      const actionTexture = new Webgl2New.Texture();
      actionTexture.loadSyncSource(retouchedImg);
      const undoTexture = this.mainImageComponent.state.texture.clone();
      const undoIsTransparentBackground = this._metaData?.isTransparentBackground || false;
      const undoCurrentBGColor = this.currentBGColor;

      this.renderer.commands.execute(
        new CommandAction(
          "retouch-ai",
          () => {
            this.mainImageComponent.state.texture = actionTexture.clone();
            this._metaData = {
              ...this._metaData,
              isAiRetouch: true,
              isTransparentBackground: false,
            };
            this.changeBGColor("#ffffff");
            this.openAdjustmentsTab();
          },
          () => {
            this.mainImageComponent.state.texture = undoTexture.clone();
            this._metaData = {
              ...this._metaData,
              isAiRetouch: false,
              isTransparentBackground: undoIsTransparentBackground,
            };
            this.changeBGColor(undoCurrentBGColor);
          },
        ),
      );

      notificationStore.closeLoader();
    });

    request.events.on("error", ({ error }) => {
      if (error.originalError?.code === ErrorCodes.MEDIA_RETOUCH_LIMIT_EXCEEDED) {
        openRetouchErrorNotification(error.originalError?.message);
      }
      notificationStore.closeLoader();
    });

    request.fetch();
  }

  public removeDust(params: { mediaId?: string }) {
    const { mediaId = null } = params;
    const { notificationStore, localeStore, userStore } = useStores();

    notificationStore.openLoader({
      loaderType: "diamond-loader",
      appearance: "secondary",
      title: localeStore.t('["edit-media"].loader.title'),
      subtitle: localeStore.t('["edit-media"].loader.subtitle'),
    });

    const oldImage = this.mainImageComponent.state.texture.textureToCanvas();
    const base64 = oldImage.toDataURL();
    const blob = base64ToBlob(base64.split(",")[1], "image/png");

    const formData = new FormData();
    formData.append("file", blob);
    if (mediaId) {
      formData.append("media_ids", mediaId.toString());
    }

    const request = postSingleRemoveDust.getRequest({ data: formData });

    request.events.on("success", async ({ success }) => {
      const { statusCode, base64 } = success;

      if (statusCode === 200) {
        // Note: silently updating userMe due to usage limits
        userStore.loadUserMeSilently();

        pushDataLayerEvent({ event: "retouch_lite" });

        const actionTexture = new Webgl2New.Texture();
        await actionTexture.loadSource("data:image/png;base64," + base64);
        const undoTexture = this.mainImageComponent.state.texture.clone();

        this.renderer.commands.execute(
          new CommandAction(
            "remove-dust",
            () => {
              this.mainImageComponent.state.texture = actionTexture.clone();
              this._metaData = { ...this._metaData, isAiRemoveDust: true };
            },
            () => {
              this.mainImageComponent.state.texture = undoTexture.clone();
              this._metaData = { ...this._metaData, isAiRemoveDust: false };
            },
          ),
        );

        notificationStore.closeLoader();
      }
    });

    request.events.on("error", ({ error }) => {
      if (error.statusCode === 400) {
        openDustRemovalErrorNotification(error.originalError?.message);
      }
      notificationStore.closeLoader();
    });

    request.fetch();
  }

  public async aiGenerative(params: {
    type: string;
    mediaId: string | number;
    otherParam?: string;
    // style?: string;
    gender?: string;
    skinToneKey?: string;
    jewelrySize?: Object;
  }) {
    let res = null;
    if (params.type === "model_image_pro") {
      res = await graphqlClient
        .executeMutation(
          createRequest(AI_MODEL_IMAGE, {
            mediaId: +params.mediaId,
            gender: params?.gender,
            skinToneKey: params?.skinToneKey,
            jewelryType: params?.otherParam,
            jewelrySize: params?.jewelrySize || {},
          }),
        )
        .toPromise();
      this._metaData = {
        ...this._metaData,
        isAiGenerated: true,
        aiGenerateType: "ai_model_image",
      };
      return res?.data?.aiModelImageAsync;
    } else if (params.type === "lifestyle_image") {
      res = await graphqlClient
        .executeMutation(
          createRequest(AI_LIFESTYLE_IMAGE, {
            mediaId: +params.mediaId,
            templateId: params.otherParam,
          }),
        )
        .toPromise();
      this._metaData = {
        ...this._metaData,
        isAiGenerated: true,
        aiGenerateType: "ai_lifestyle_image",
        aiGenerateOptions: { backgroundFileUrl: params.otherParam },
      };
      return res?.data?.aiLifestyleImageAsync;
    } else if (params.type === "change_gold_color") {
      res = await graphqlClient
        .executeMutation(
          createRequest(AI_CHANGE_METAL_MATERIAL, {
            mediaId: +params.mediaId,
            targetMetalColorKey: params.otherParam,
          }),
        )
        .toPromise();
      this._metaData = {
        ...this._metaData,
        isAiGenerated: true,
        aiGenerateType: "ai_change_metal_material",
        aiGenerateOptions: { backgroundFileUrl: params.otherParam },
      };
      return res?.data?.aiChangeMetalMaterialAsync;
    }
  }

  public async fetchAiGenerativeResult(taskId: String) {
    const { renderer } = editMediaStore;

    const res = await graphqlClient
      .executeQuery(
        createRequest(AI_TASK_STATE, {
          taskId,
        }),
      )
      .toPromise();
    const url = res?.data?.aiTaskState?.resultMedia?.file?.original;
    if (url) {
      this.renderer.commands.clear();

      const actionTexture = new Webgl2New.Texture();
      await actionTexture.loadSource(url);
      const size = new Vec2(
        actionTexture?.textureSize?.width || 1024,
        actionTexture?.textureSize?.height || 1024,
      );
      renderer.setCanvasSize(size, true);
      this.mainImageComponent.transform.calcDimensions({
        position: size.getHalf(),
        size: size,
        scale: new Vec2(1, 1),
        rotation: 0,
      });

      this.mainImageComponent.state.texture = actionTexture;
      this.renderer.selectComponent(this.mainImageComponent);
      this.toggleGenerativeAI(true);
    }
    return res?.data?.aiTaskState;
  }

  public async assignMediasToSku(sourceMediaId: number, aiMediaId: number) {
    const skuRes = await graphqlClient
      .executeMutation(
        createRequest(PRODUCT_GEN_SKU, {
          productType: "",
        }),
      )
      .toPromise();
    const sku = skuRes?.data?.productGenSku?.data;
    console.log("assignMediasToSku", sku, sourceMediaId, aiMediaId);
    const res = await graphqlClient
      .executeMutation(
        createRequest(MEDIA_SET_SKU, {
          ids: [sourceMediaId, aiMediaId],
          sku,
        }),
      )
      .toPromise();
    return res;
  }

  // public async fetchAiGenerativeResult(taskId: String, metaData: Object) {
  //   const { userStore } = useStores();
  //   const { renderer } = editMediaStore;
  //   const base64 = ''
  //   if (base64) {
  //     // Note: silently updating userMe due to usage limits
  //     userStore.loadUserMeSilently();

  //     // pushDataLayerEvent({ event: "modelImage_lite" });
  //     const actionTexture = new Webgl2New.Texture();
  //     await actionTexture.loadSource("data:image/jpeg;base64," + base64);
  //     const undoTexture = this.mainImageComponent.state.texture.clone();

  //     this.renderer.commands.execute(
  //       new CommandAction(
  //         "ai-generative",
  //         () => {
  //           const size = new Vec2(
  //             actionTexture?.textureSize?.width || 1024,
  //             actionTexture?.textureSize?.height || 1024,
  //           );
  //           renderer.setCanvasSize(size, true);

  //           this.mainImageComponent.transform.setSize(size);
  //           this.mainImageComponent.state.texture = actionTexture.clone();
  //           this.renderer.selectComponent(this.mainImageComponent);
  //           this.toggleGenerativeAI(true);
  //         },
  //         () => {
  //           const size = new Vec2(
  //             undoTexture?.textureSize?.width,
  //             undoTexture?.textureSize?.height,
  //           );
  //           renderer.setCanvasSize(size, true);

  //           this.mainImageComponent.transform.setSize(size);
  //           this.mainImageComponent.state.texture = undoTexture.clone();
  //           this.renderer.selectComponent(this.mainImageComponent);
  //           this.toggleGenerativeAI(false);
  //         },
  //       ),
  //     );
  //     return true;
  //   }
  //   return false;
  // }

  /* Requests ↑ */

  /* UI State ↓ */
  public mount(parent: HTMLElement, mediaId: string, locationState: EditMediaPageHistoryState) {
    runInAction(() => {
      this.renderer = new Webgl2New.Webgl2dRenderer();
      this.renderer.setRulersEnabled(this._rulersEnabled);
      this._isBGRemoved = locationState?.hasRemovedBackground || false;
    });

    return Webgl2New.Webgl2.initialize().then(() => {
      this.loadMedia(mediaId, locationState);
      this.renderer.mount(parent, parent.clientWidth, parent.clientHeight);
      this._handleInitializeWebgl2();
    });
  }

  public unmount() {
    this.renderer.unmount();
    this._initStore();
  }

  public openTab(tab: EditMediaTabType): boolean {
    if (
      this._tabBlockCallback?.({
        tab,
        unregister: () => this.unregisterTabBlockCallback(),
        proceed: () => this._tabProceedCallback?.(),
      })
    ) {
      return false;
    }

    this._currentTab = tab;
    return true;
  }

  public registerTabBlockCallback(callback: EditMediaTabBlockCallback) {
    this._tabBlockCallback = callback;
    return () => this.unregisterTabBlockCallback();
  }

  public unregisterTabBlockCallback() {
    this._tabBlockCallback = null;
  }

  /**
   * Run an action but respect current tab "block" handler (crop/resize/measurements unsaved states).
   * This is useful for flows that are NOT tab switches (e.g. exit/close editor).
   *
   * Returns true if blocked (the block handler is responsible to call `proceed()` later).
   */
  public proceedWithTabBlock(proceed: VoidFunction, tab: EditMediaTabType = this._currentTab) {
    const shouldBlock = this._tabBlockCallback?.({
      tab,
      unregister: () => this.unregisterTabBlockCallback(),
      proceed,
    });

    if (shouldBlock) return true;

    proceed();
    return false;
  }

  public changeBGColor(color: string) {
    this.currentBGColor = color;
    this.tempBGColor = color;
    this.backgroundComponent.state.color = hexToRgba(color, true);
  }

  public setTempColor(color: string) {
    this.tempBGColor = color;
    this.backgroundComponent.state.color = hexToRgba(color, true);
  }

  public addBGColor(color: string) {
    const hasSameColor = this.bgColors.includes(color);
    if (hasSameColor) return this.changeBGColor(color);

    this.currentBGColor = color;
    this.tempBGColor = color;
    this.backgroundComponent.state.color = hexToRgba(color, true);
    const nextAddedColors = this._normalizeAddedColors(this._addedColors.concat(color));
    this._addedColors = nextAddedColors;
    this._saveStoredBGColors(nextAddedColors);
  }

  public async uploadBGImage(file: File) {
    runInAction(() => {
      this._isProcessingImage = true;
    });

    const { image, imageUrl } = await loadImageUrl(file);

    runInAction(() => {
      this._isProcessingImage = false;
    });

    const newTexture = new Webgl2New.Texture();
    newTexture.loadSyncSource(image);

    this.backgroundImageComponent.transform.calcDimensions({
      position: this.renderer.canvasGlobalCenter,
      size: new Vec2(image.width, image.height),
    });

    this.currentUploadedBG = imageUrl;
    this.uploadedBGs = this.uploadedBGs.concat(imageUrl);
    this.backgroundImageComponent.transform.copy(this.backgroundImageComponent.transform.clone());
    this.backgroundImageComponent.state.texture = newTexture;
    this.backgroundImageComponent.style.display = "initial";
    this.renderer.selectComponent(this.backgroundImageComponent);
  }

  private _selectSameBGImage() {
    this.currentUploadedBG = "";
    this.backgroundImageComponent.style.display = "none";
    this.renderer.selectComponent(null);
  }

  public async selectBGImage(objectUrl: string) {
    if (this.currentUploadedBG === objectUrl) {
      if (this.renderer.selectedComponent === (this.backgroundImageComponent as any)) {
        return this._selectSameBGImage();
      } else {
        return this.renderer.selectComponent(this.backgroundImageComponent);
      }
    }

    runInAction(() => {
      this._isProcessingImage = true;
    });

    const image = await loadImage(objectUrl);

    runInAction(() => {
      this._isProcessingImage = false;
    });

    const newTexture = new Webgl2New.Texture();
    newTexture.loadSyncSource(image);

    this.backgroundImageComponent.transform.calcDimensions({
      position: this.renderer.canvasGlobalCenter,
      size: new Vec2(image.width, image.height),
    });

    this.currentUploadedBG = objectUrl;
    this.backgroundImageComponent.transform.copy(this.backgroundImageComponent.transform.clone());
    this.backgroundImageComponent.state.texture = newTexture;
    this.backgroundImageComponent.style.display = "initial";
    this.renderer.selectComponent(this.backgroundImageComponent);
  }

  public setStraightenMode(value: boolean) {
    this._measurementsStraightenMode = value;
    this.fantomMeasurement.setMeasurementsStraightenMode(value);
    this.measurementsArr.forEach((measurement) => {
      measurement.setMeasurementsStraightenMode(value);
    });
  }

  public setRulersEnabled(value: boolean) {
    this._rulersEnabled = value;
    this.renderer?.setRulersEnabled(value);
  }

  public openCrop() {
    this._isInCrop = true;
    this.renderer.commands.events.block();
    this.renderer.selectComponent(this.mainImageComponent);
    this.renderer.eventsManager.assignTool(this.renderer.tools.cropTool);
  }

  public closeCrop() {
    this._isInCrop = false;
    this.renderer.commands.events.unblock();
    this.renderer.eventsManager.assignTool(this.renderer.tools.handSelectionTool);
  }

  public handleCrop() {
    this.renderer.commands.events.unblock();
    this.renderer.tools.cropTool.handleCrop();
    this.closeCrop();
  }

  public openAdjustmentsTab() {
    if (this._tabBlockCallback) this._tabProceedCallback = () => this.openAdjustmentsTab();
    if (!this.openTab("adjustments")) return;
    this.renderer.eventsManager.assignTool(this.renderer.tools.handSelectionTool);
  }

  public openMeasurementsTab() {
    if (this._tabBlockCallback) this._tabProceedCallback = () => this.openMeasurementsTab();
    if (!this.openTab("measurements")) return;
    this.renderer.commands.events.block();
    this.renderer.selectComponent(this.fantomMeasurement);
    this.renderer.eventsManager.assignTool(this.renderer.tools.measurementTool);
  }

  public closeMeasurementsTab() {
    this.openTab("adjustments");
    this.renderer.commands.events.unblock();
    this.renderer.eventsManager.assignTool(this.renderer.tools.handSelectionTool);
    this.renderer.selectComponent(this.mainImageComponent);
    this._resetFantomMeasurement();
  }

  public openResizeTab() {
    if (this._tabBlockCallback) this._tabProceedCallback = () => this.openResizeTab();
    if (!this.openTab("resize")) return;
    this.renderer.commands.events.block();
    this.renderer.selectComponent(null);
    this.renderer.eventsManager.assignTool(this.renderer.tools.canvasResizeTool);
  }

  public closeResizeTab() {
    this.openTab("adjustments");
    this.renderer.commands.events.unblock();
    this.renderer.eventsManager.assignTool(this.renderer.tools.handSelectionTool);
    this.renderer.selectComponent(this.mainImageComponent);
  }

  public openWatermarkTab() {
    if (this._tabBlockCallback) this._tabProceedCallback = () => this.openWatermarkTab();
    if (!this.openTab("watermark")) return;
    this.renderer.commands.events.unblock();
    this.renderer.eventsManager.assignTool(this.renderer.tools.handSelectionTool);
  }

  public openBackgroundTab() {
    if (this._tabBlockCallback) this._tabProceedCallback = () => this.openBackgroundTab();
    if (!this.openTab("background")) return;
    this.renderer.commands.events.unblock();
    this.renderer.eventsManager.assignTool(this.renderer.tools.handSelectionTool);
  }

  public closeBackgroundTab() {
    this.openTab("adjustments");
    this.renderer.commands.events.unblock();
    this.renderer.eventsManager.assignTool(this.renderer.tools.handSelectionTool);
    this.renderer.selectComponent(this.mainImageComponent);
    this._inBGAtFirst = false;
  }

  public openMediaInfoTab() {
    if (this._tabBlockCallback) this._tabProceedCallback = () => this.openMediaInfoTab();
    this.openTab("media-info");
  }

  public chessGridComponentDisplay(display: "initial" | "none") {
    runInAction(() => {
      this.renderer.chessGridComponent.bgColor = display === "initial" ? "" : "#ffffff";
      this.renderer.render();
    });
  }

  /* UI State ↑ */

  /* Helpers ↓ */
  private _initStore() {
    this._currentTab = "adjustments";
    this._tabBlockCallback = null;
    this._tabProceedCallback = null;

    this._loading = true;
    this._isBGRemoved = false;
    this._inBGAtFirst = false;
    this._isInCrop = false;

    this.mediaShortModel = null as any;
    this.renderer = null as any;

    this._addedColors = this._loadStoredBGColors();
    this.currentBGColor = this._defaultColors[2];
    this.tempBGColor = this.currentBGColor;
    this.uploadedBGs = [];
    this.currentUploadedBG = "";
    this._isProcessingImage = false;

    this.measurementColors = ["#0d0d0d", "#ffffff"];
    this.measurementsArr = [];

    this.backgroundComponent = null as any;
    this.renderer?.events?.offEvent("canvasTransform");

    this._measurementsStraightenMode = true;
    this._rulersEnabled = false;

    this.backgroundImageComponent = null as any;
    this.mainImageComponent = null as any;
    this.measurementsComponent = null as any;

    this._wants300DpiExport = false;
  }

  private _resetFantomMeasurement() {
    this.fantomMeasurement = new Webgl2New.MeasurementComponent({
      color: this.measurementColors[0],
      straightenMode: this.measurementsStraightenMode,
    });
    this._currentMeasurement = this.fantomMeasurement;
  }

  private _handleInitializeWebgl2() {
    // Background Color Component ↓
    const backgroundComponent = new Webgl2New.BackgroundComponent(
      hexToRgba(this.currentBGColor, true),
    );
    this.backgroundComponent = backgroundComponent;
    this.renderer.events.on("canvasTransform", () => {
      this.backgroundComponent.transform.copy(this.renderer.chessGridComponent.transform);
    });
    backgroundComponent.transform.setSize(this.renderer.canvasSize);
    backgroundComponent.style.pointerEvents = "none";
    this.renderer.addComponent(backgroundComponent);
    // Background Color Component ↑

    // Background Image Component ↓
    const backgroundImageTexture = new Webgl2New.Texture();
    const backgroundImageComponent = new Webgl2New.ImageAdjustRectComponent(backgroundImageTexture);
    this.backgroundImageComponent = backgroundImageComponent;
    backgroundImageComponent.style.display = "none";
    this.renderer.addComponent(backgroundImageComponent);
    // Background Image Component ↑

    // Main Image Component ↓
    const mainImageTexture = new Webgl2New.Texture();
    const mainImageComponent = new Webgl2New.ImageAdjustRectComponent(mainImageTexture);
    this.mainImageComponent = mainImageComponent;
    this.renderer.addComponent(mainImageComponent);
    // Main Image Component ↑

    // Fantom Measurement Component ↓
    this._resetFantomMeasurement();
    // Fantom Measurement Component ↑

    // Measurements Component ↓
    const measurementsComponent = new Webgl2New.MeasurementsComponent();
    this.measurementsComponent = measurementsComponent;
    this.renderer.addComponent(measurementsComponent);
    // Measurements Component ↑
    this.chessGridComponentDisplay("none");
  }

  private _normalizeAddedColors(colors: string[]) {
    const maximumAddedColors = 10;
    const sanitized = colors
      .filter((color): color is string => typeof color === "string")
      .map((color) => color.trim())
      .filter(Boolean)
      .filter((color) => !this._defaultColors.includes(color));

    const unique: string[] = [];
    for (const color of sanitized) {
      if (unique.includes(color)) continue;
      unique.push(color);
    }

    return unique.slice(-maximumAddedColors);
  }

  private _loadStoredBGColors() {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(BG_CUSTOM_COLORS_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return this._normalizeAddedColors(parsed);
    } catch {
      return [];
    }
  }

  private _saveStoredBGColors(colors: string[]) {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(BG_CUSTOM_COLORS_STORAGE_KEY, JSON.stringify(colors));
    } catch {
      // ignore storage failures
    }
  }

  /* Helpers ↑ */
}

export const editMediaStore = new EditMediaStore();
